import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BlogEditor from '../components/BlogPostEditor/BlogEditor';
import { getPostById, createPost, updatePost, slugify } from '../utils/blogService';
import { uploadFile } from '../utils/storageService';
import { supabase } from '../utils/supabaseClient';
import './BlogPostEditor.css';

function getCurrentUserName() {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return '';
    const user = JSON.parse(raw);
    return user?.name || '';
  } catch (_) {
    return '';
  }
}

export default function BlogPostEditor() {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const coverInputRef = useRef(null);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [status, setStatus] = useState('draft');

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const post = await getPostById(id);
        if (cancelled) return;
        if (!post) {
          setError('Пост не найден.');
          return;
        }
        setTitle(post.title || '');
        setSlug(post.slug || '');
        setSlugTouched(true);
        setExcerpt(post.excerpt || '');
        setContent(post.content || '');
        setCoverImageUrl(post.coverImageUrl || '');
        setStatus(post.status || 'draft');
      } catch (e) {
        console.error('Error loading post:', e);
        setError('Не удалось загрузить пост.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isEditing]);

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const handleCoverPick = () => coverInputRef.current?.click();
  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingCover(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('Войдите в аккаунт, чтобы загрузить обложку.');
        return;
      }
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `teachers/${authUser.id}/blog/${id || 'new'}/cover-${Date.now()}.${ext}`;
      const url = await uploadFile(file, path);
      setCoverImageUrl(url);
    } catch (err) {
      console.error('Error uploading cover image:', err);
      alert('Не удалось загрузить обложку. Попробуйте ещё раз.');
    } finally {
      setUploadingCover(false);
    }
  };

  const save = async (nextStatus) => {
    if (!title.trim()) {
      setError('Введите заголовок поста.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim(),
        content,
        coverImageUrl: coverImageUrl || null,
        authorName: getCurrentUserName(),
        status: nextStatus,
      };
      if (isEditing) {
        await updatePost(id, payload);
      } else {
        await createPost(payload);
      }
      navigate('/blog/admin');
    } catch (e) {
      console.error('Error saving post:', e);
      setError('Не удалось сохранить пост. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="blog-post-editor-page"><p>Загрузка…</p></div>;
  }

  return (
    <div className="blog-post-editor-page">
      <div className="blog-post-editor-header">
        <h1>{isEditing ? 'Редактировать пост' : 'Новый пост'}</h1>
        <div className="blog-post-editor-actions">
          <button className="blog-btn blog-btn-ghost" onClick={() => navigate('/blog/admin')} disabled={saving}>
            Отмена
          </button>
          <button className="blog-btn blog-btn-secondary" onClick={() => save('draft')} disabled={saving}>
            Сохранить черновик
          </button>
          <button className="blog-btn blog-btn-primary" onClick={() => save('published')} disabled={saving}>
            {status === 'published' ? 'Сохранить изменения' : 'Опубликовать'}
          </button>
        </div>
      </div>

      {error && <div className="blog-post-editor-error">{error}</div>}

      <div className="blog-post-editor-field">
        <label>Заголовок</label>
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Заголовок поста"
          className="blog-post-editor-title-input"
        />
      </div>

      <div className="blog-post-editor-field">
        <label>Ссылка (slug)</label>
        <div className="blog-post-editor-slug-row">
          <span>/blog/</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
          />
        </div>
      </div>

      <div className="blog-post-editor-field">
        <label>Краткое описание (для карточки в списке)</label>
        <textarea
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Пара предложений о посте…"
          rows={2}
        />
      </div>

      <div className="blog-post-editor-field">
        <label>Обложка</label>
        {coverImageUrl && (
          <div className="blog-post-editor-cover-preview">
            <img src={coverImageUrl} alt="Обложка поста" />
            <button type="button" className="blog-btn blog-btn-ghost" onClick={() => setCoverImageUrl('')}>
              Убрать
            </button>
          </div>
        )}
        <button type="button" className="blog-btn blog-btn-secondary" onClick={handleCoverPick} disabled={uploadingCover}>
          {uploadingCover ? 'Загрузка…' : coverImageUrl ? 'Заменить обложку' : 'Загрузить обложку'}
        </button>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleCoverChange}
        />
      </div>

      <div className="blog-post-editor-field">
        <label>Текст поста</label>
        <BlogEditor content={content} onChange={setContent} postId={id} />
      </div>
    </div>
  );
}
